/**
 * The trend detail chart's pure model — series routing and axis arithmetic
 * (REQ-STATUS-075; feature spec ``system-status-trend-detail`` §2.3, §9.1).
 *
 * Kept apart from the component because the geometry is where this mark is
 * right or wrong, and the rendering is a thin projection of it. Every function
 * here is a **pure function of its arguments** — nothing reads the clock, the
 * container, or the DOM — which is what makes the chart's behaviour assertable
 * without one.
 *
 * # Three marks wearing one name
 *
 * "The trend detail chart" is really three, chosen by the metric's unit
 * (:func:`unitKind`), and :func:`buildDetailSeries` is where that choice is
 * made **once** so no downstream caller has to re-derive it:
 *
 *   - a **magnitude** line, for the six measurable units;
 *   - a **band**, for ``boolean`` — a boolean over time is a *state*, and a
 *     line between `true` and `false` implies an interpolation that does not
 *     exist;
 *   - a magnitude line over a **derived age**, for ``epoch_seconds`` — see
 *     :func:`buildDetailSeries`.
 *
 * Cross-references:
 *   - REQ-STATUS-073 (a gap is a gap — it survives every transform here).
 *   - REQ-STATUS-078 (the tick counts these functions accept; the *rule* that
 *     picks them from the container width lands with the responsive phase).
 *   - `design-system.md` → Component patterns → Trend detail chart.
 */
import type { TrendPoint } from "@/system-status/status-detail-primitives";
import {
  ageAtBucket,
  formatUnitValue,
  formatZuluTick,
  formatZuluTimestamp,
  unitKind,
  type MetricUnit,
} from "@/system-status/metric-units";
import type { HistoryWindow } from "@/system-status/use-component-history";

/** One axis tick: where it sits (0..1 along the axis) and what it reads. */
export interface AxisTick {
  /** Position along the axis, ``0`` at the start / top, ``1`` at the end / bottom. */
  at: number;
  /** The rendered label. Never truncated and never rotated (REQ-STATUS-078). */
  text: string;
}

/** The inclusive value range an axis spans. */
export interface ValueRange {
  min: number;
  max: number;
}

/** A series that can sit on a numeric axis. */
export interface MagnitudeSeries {
  kind: "magnitude";
  /** Plottable points; ``null`` is a gap and stays one. */
  points: readonly TrendPoint[];
  /** The unit the axis labels with — **rewritten** for a derived series. */
  unit: MetricUnit | undefined;
  /** The visible label — **rewritten** for a derived series. */
  label: string;
  /**
   * Whether the plotted values were computed rather than measured. Only the
   * ``epoch_seconds`` age sets this, and it is why the label is rewritten.
   */
  derived: boolean;
}

/** A series of ordinal states, drawn as a band. */
export interface BandSeries {
  kind: "band";
  /** ``[time, state]`` per bucket; ``"gap"`` where no sample was taken. */
  points: readonly (readonly [number, string])[];
  label: string;
}

export type DetailSeries = MagnitudeSeries | BandSeries;

/** The state token a boolean band uses for an absent sample. */
export const BAND_GAP = "gap";

/**
 * Route one series to the mark that can carry it, transforming where needed.
 *
 * # ``boolean`` becomes a band
 *
 * Mapped to named states rather than left as 1 and 0, so nothing downstream
 * has to remember which way round they were. **A gap becomes its own state**,
 * never a `no`: conflating them would assert a measurement that was never
 * taken (REQ-STATUS-073).
 *
 * # ``epoch_seconds`` becomes an age
 *
 * A "last run" timestamp plotted as a magnitude is a staircase climbing to the
 * present — honest, and useless. The age (:func:`ageAtBucket`) is a **sawtooth
 * resetting to zero on every run**, which makes the *cadence* and the *missed
 * run* visible at a glance.
 *
 * Two guards travel with the transform, because this is the one place the
 * display **derives** rather than displays:
 *
 *   - the **label is rewritten** to say what is plotted (*"Time since …"*), so
 *     nobody reads the axis as the timestamp;
 *   - the **unit is rewritten** to ``seconds``, so the axis reads ``1h 5m``
 *     rather than a ten-digit epoch.
 *
 * The age itself is computed against **each bucket's own time**, never against
 * ``now`` — that guard lives in :func:`ageAtBucket`.
 */
export function buildDetailSeries({
  points,
  label,
  unit,
}: {
  points: readonly TrendPoint[];
  label: string;
  unit: MetricUnit | undefined;
}): DetailSeries {
  switch (unitKind(unit)) {
    case "boolean":
      return {
        kind: "band",
        label,
        points: points.map(([time, value]) => [
          time,
          value === null ? BAND_GAP : value === 0 ? "no" : "yes",
        ]),
      };
    case "epoch":
      return {
        kind: "magnitude",
        label: `Time since ${lowerFirst(label)}`,
        unit: "seconds",
        derived: true,
        points: points.map(([time, value]) => [
          time,
          value === null ? null : ageAtBucket(time, value),
        ]),
      };
    default:
      return { kind: "magnitude", points, unit, label, derived: false };
  }
}

/**
 * The range the y axis spans — **the data's own, never forced to zero**.
 *
 * Zeroing the axis would compress a CPU line moving 40 → 42 % into a flat mark
 * at the top of the plot, which is exactly the reading an operator expanded
 * the chart to avoid. The cost — that a non-zero baseline can exaggerate a
 * small movement — is paid by *always labelling the range* rather than by
 * distorting the plot (REQ-STATUS-075).
 *
 * A flat series is widened by a whole unit so the caller never divides by a
 * zero span; ``null`` means nothing is known and there is nothing to draw.
 */
export function valueRange(points: readonly TrendPoint[]): ValueRange | null {
  const known = points.flatMap(([, value]) => (value === null ? [] : [value]));
  if (known.length === 0) return null;
  const min = Math.min(...known);
  const max = Math.max(...known);
  return max > min ? { min, max } : { min, max: min + 1 };
}

/**
 * Y ticks from the top of the plot down: max first, min last.
 *
 * Both ends are always emitted whatever the count, because they **are** the
 * range disclosure — a chart that drops its min or max at a narrow width stops
 * saying what it is showing (REQ-STATUS-075, REQ-STATUS-078).
 *
 * @param count - How many ticks to place, at least 2. The responsive rule that
 *   chooses this from the container's width lands with REQ-STATUS-078.
 */
export function yAxisTicks(
  range: ValueRange,
  count: number,
  unit: MetricUnit | undefined,
): AxisTick[] {
  const steps = Math.max(2, Math.floor(count));
  return Array.from({ length: steps }, (_unused, index) => {
    const at = index / (steps - 1);
    // ``at`` runs top-to-bottom while values run bottom-to-top: SVG y grows
    // downward, so the first tick is the MAXIMUM.
    const value = range.max - at * (range.max - range.min);
    return { at, text: formatUnitValue(value, unit) };
  });
}

/**
 * X ticks as **absolute ZULU** timestamps, evenly spaced across the series.
 *
 * The first and last points anchor the ends of the axis, so a tick always
 * names the window's real bounds rather than an interpolated instant.
 *
 * Never more ticks than there are points: two ticks reading the same timestamp
 * is a collision the responsive rule cannot fix, because **both labels are
 * correct** — the only repair is to ask for fewer.
 */
export function xAxisTicks(
  points: readonly TrendPoint[],
  window: HistoryWindow,
  count: number,
): AxisTick[] {
  if (points.length === 0) return [];
  if (points.length === 1) return [{ at: 0, text: formatZuluTick(points[0][0], window) }];
  // A single tick is the narrow-pane case (see `tickCountsForWidth`): it names
  // the LAST instant, anchored right, because the window selector already
  // states the span and the most recent reading is what a pane on a wall
  // display is asked for.
  if (Math.floor(count) <= 1) {
    return [{ at: 1, text: formatZuluTick(points[points.length - 1][0], window) }];
  }
  const steps = Math.min(Math.max(2, Math.floor(count)), points.length);
  return Array.from({ length: steps }, (_unused, index) => {
    const at = index / (steps - 1);
    const pointIndex = Math.round(at * (points.length - 1));
    return { at, text: formatZuluTick(points[pointIndex][0], window) };
  });
}

/** The collector's warn/crit pair, as carried on the catalog entry. */
export interface MetricThreshold {
  warn: number;
  crit: number;
}

/** One threshold reference line: where it sits, what it reads, how it dashes. */
export interface ThresholdMark {
  kind: "warn" | "crit";
  /** Position from the top of the plot, matching :func:`yAxisTicks`. */
  at: number;
  /** Edge label — names the LEVEL, so colour is never the only channel. */
  text: string;
  /** SVG ``stroke-dasharray``. The second non-colour channel. */
  dash: string;
  /** SVG ``stroke``. Redundant reinforcement, never the sole carrier. */
  stroke: string;
}

/**
 * Widen a range so both thresholds fall inside it (REQ-STATUS-076).
 *
 * **A crit line off-screen is worse than a slightly compressed series.** A
 * reader cannot tell a threshold that is above the data from one that simply
 * was not drawn, and the first is the reading the chart exists to give.
 *
 * Returns the range untouched when the metric carries no threshold — which is
 * most of them; only the ~7 rollup-driving metrics have one.
 */
export function extendRangeForThresholds(
  range: ValueRange,
  threshold: MetricThreshold | undefined,
): ValueRange {
  if (threshold === undefined) return range;
  return {
    min: Math.min(range.min, threshold.warn, threshold.crit),
    max: Math.max(range.max, threshold.warn, threshold.crit),
  };
}

/**
 * The reference lines for one metric, or none where it cannot escalate.
 *
 * # Three channels, and colour is the disposable one
 *
 * REQ-UI-046 forbids colour-alone encoding, so each mark carries its meaning
 * twice over before the colour is applied: the **edge label names the level**
 * (`warn 80%`, `crit 95%`), and **the two dash patterns differ**. A greyscale
 * screenshot, or a reader who cannot separate amber from red, loses nothing.
 *
 * # The values are the collector's own
 *
 * They arrive on the catalog entry, pinned against
 * ``meridian_status_collector``'s own ``StatusThresholds`` by the
 * REQ-STATUS-058 coverage guard. The chart therefore re-evaluates **the same
 * numbers the stoplight used**, so the line and the pill cannot disagree —
 * which is the whole argument for surfacing `threshold` on the resolved metric
 * rather than parsing the rollup's reason sentence.
 *
 * ⚠ **Colours are raw ``var(--status-*)`` tokens, not Tailwind classes.** That
 * family is the panel-scoped one (the System Status pane overrides
 * ``--status-degraded`` back to the classic stoplight amber per the
 * design-system OQ-5 note), and it sidesteps the class-generation trap
 * entirely: the generated utility is ``text-meridian-status-degraded``, and the
 * plausible-looking ``text-status-degraded`` produces **no colour at all** —
 * silently, with no compile error and no failing test.
 */
export function thresholdMarks(
  range: ValueRange,
  threshold: MetricThreshold | undefined,
  unit: MetricUnit | undefined,
): ThresholdMark[] {
  if (threshold === undefined) return [];
  const span = range.max - range.min || 1;
  const place = (value: number): number => (range.max - value) / span;
  return [
    {
      kind: "warn",
      at: place(threshold.warn),
      text: `warn ${formatUnitValue(threshold.warn, unit)}`,
      dash: "4 4",
      stroke: "var(--status-degraded)",
    },
    {
      kind: "crit",
      at: place(threshold.crit),
      text: `crit ${formatUnitValue(threshold.crit, unit)}`,
      // A longer dash than `warn`, so severity is legible from the LINE and
      // not only from its label or its colour.
      dash: "10 3",
      stroke: "var(--status-unhealthy)",
    },
  ];
}

/** How many ticks each axis places at a given container width. */
export interface TickCounts {
  x: number;
  y: number;
}

/**
 * The responsive tick rule (REQ-STATUS-078).
 *
 * The chart appears at three very different widths — a maximized panel
 * (~1400 px), a docked Selection Detail pane (~350 px), and inside a
 * **connection drawer**, narrower still. Because the x axis carries **absolute
 * ZULU** (REQ-STATUS-075), its labels are roughly 3× the width of the relative
 * ones first proposed, so tick collision is the *expected* failure at narrow
 * widths rather than an edge case.
 *
 * | Container width | X ticks | Y ticks |
 * |---|---|---|
 * | ≥ 640 px | one per ~200 px, floor 2, cap 8 | 3 |
 * | 360–639 px | 2 | 3 |
 * | 260–359 px | 2 | 2 |
 * | < 260 px | **1** | 2 |
 *
 * ⚠ **The single-tick band was added by the §5.11 live pass (2026-08-03), and
 * only a live pass could have found it.** The rule originally floored at two,
 * on the reasoning that two ticks *are* the axis. Measured in a real engine at
 * a 520 px viewport, the plot is ~81 px wide and two `HH:MMZ` labels are ~48 px
 * each — **they overlapped by 15 px**. Two ticks need roughly 104 px of plot,
 * and the gutter plus padding costs ~68 px of the container, so a container
 * under ~172 px cannot seat two. 260 px is the conservative floor: it also
 * seats the wider dated labels the 24 h and 7 d windows carry.
 *
 * The surviving tick is the **last**, not the first. The window selector beside
 * the chart already states the span, so the earliest instant is derivable; the
 * most recent one is not, and on a pane left open on a wall display it is the
 * thing an operator needs — *how fresh is this?*
 *
 * # Only the axis that gains room gains ticks
 *
 * The y count never scales. The plot's height is fixed at ~200 px and does not
 * grow when the panel is maximized, so a wider pane buys nothing there.
 *
 * # The x count scales rather than sitting at a constant
 *
 * Predictability is the argument for a constant, and it loses: the reason an
 * operator maximizes a panel is to read the chart **more precisely**, and a
 * rule that ignores the extra width defeats the gesture. This is still a **pure
 * function of width** — exactly as deterministic and as testable as a constant,
 * and *monotonic*, so a wider pane can never show fewer ticks.
 *
 * ⚠ Ticks are **dropped, never rotated and never truncated** — that rule lives
 * with the renderer, but it is why this function returns *fewer* ticks rather
 * than a smaller font or an angle.
 */
export function tickCountsForWidth(width: number): TickCounts {
  if (!Number.isFinite(width) || width < 260) return { x: 1, y: 2 };
  if (width < 360) return { x: 2, y: 2 };
  if (width < 640) return { x: 2, y: 3 };
  return { x: Math.min(8, Math.max(2, Math.floor(width / 200))), y: 3 };
}

/**
 * The bucket under a pointer at ``fraction`` across the plot (REQ-STATUS-077).
 *
 * **Nearest, not truncated.** ``Math.floor`` would bias every reading one
 * bucket earlier across the right half of each cell — a *wrong* timestamp
 * rather than an imprecise one, on a chart whose whole purpose is telling an
 * operator *when* something happened.
 *
 * Clamps rather than trusting its input: a pointer can leave the plot mid-drag,
 * and a fraction outside `[0, 1]` must resolve to an end bucket, never index
 * past the array.
 */
export function bucketAtFraction(count: number, fraction: number): number {
  // ⚠ A non-finite fraction must not escape as a non-finite INDEX. `Math.min`
  // and `Math.max` both propagate NaN, so an unguarded clamp yields NaN, and
  // `points[NaN]` is `undefined` — which crashes the render rather than
  // producing a wrong reading. Real browsers always supply a coordinate; a
  // white panel is too high a price for trusting that.
  if (count <= 1 || !Number.isFinite(fraction)) return 0;
  const clamped = Math.min(1, Math.max(0, fraction));
  return Math.round(clamped * (count - 1));
}

/**
 * What the crosshair and the keyboard cursor say about one bucket
 * (REQ-STATUS-077).
 *
 * # A gap says so
 *
 * REQ-STATUS-073's honesty rule governs the cursor as well as the line. A
 * cursor that steps silently over a missing bucket asserts exactly the
 * continuity the line itself refuses to draw, and the operator reading it has
 * no way to tell "nothing was measured" from "you skipped it".
 *
 * # A derived series carries its raw instant too
 *
 * For an ``epoch_seconds`` age, the **age** is what is plotted and the
 * **instant** is what an operator quotes to someone else, so the reading gives
 * both (§9.1). The raw instant is recovered as ``bucket_time − age`` rather
 * than carried alongside: there is only one number, so it cannot drift from
 * itself.
 *
 * @param point - The bucket, ``[epoch_seconds, value | null]``.
 * @param unit - The **series'** unit, already rewritten for a derived series.
 * @param derived - Whether the plotted value was computed rather than measured.
 */
export function formatReading(
  point: TrendPoint,
  unit: MetricUnit | undefined,
  derived: boolean,
): string {
  const [time, value] = point;
  const at = formatZuluTimestamp(time);
  if (value === null) return `${at} — no sample`;
  const reading = `${at} — ${formatUnitValue(value, unit)}`;
  if (!derived) return reading;
  return `${reading} (last run ${formatZuluTimestamp(time - value)})`;
}

/** Lower-case the first character, so a label composes into a sentence. */
function lowerFirst(text: string): string {
  return text.length === 0 ? text : text[0].toLowerCase() + text.slice(1);
}
